import React, { useEffect, useRef } from 'react';

/* React Redux */
import { useDispatch, useSelector } from 'react-redux';

/* Material UI */
import { CircularProgress, Grid, InputAdornment, TextField } from '@mui/material';

/* Project */
import { anyFalseInObject, anyTruhtyInObject } from 'utils/functions';
import { darkerGreyOnForms } from 'utils/helper';
import {
  controlsChanged,
  getUserVehicleWithConectaApi,
  setControlsErrors,
} from '../../schedulerSteps.actions';
import SchedulerStepsStyles from '../../SchedulerSteps.styles';

const SEARCHING_MESSAGE = 'Buscando datos del vehículo';

const searchingHelperText = (searchingVehicle) => (searchingVehicle ? SEARCHING_MESSAGE : '');

const patentHelperText = (patentError, searchingVehicle) =>
  patentError || searchingHelperText(searchingVehicle);

/* The helper text announces the search; the spinner is only visual. */
const searchingAdornment = (searchingVehicle) =>
  searchingVehicle ? (
    <InputAdornment position="end">
      <CircularProgress size={18} aria-hidden="true" />
    </InputAdornment>
  ) : null;

// TODO: SET FIRENT MESSAGE ON PATENT ERRORS
const patentRequiredFieldValidation = (patent) => {
  const validations = {
    notEmptyValue: patent === '',
    notEmptyValueWithTrim: patent.trim().length === 0,
  };

  const isInvalidField = anyTruhtyInObject(validations);

  return isInvalidField ? 'Campo patente requerido' : '';
};

const patentFormatRequiredFieldValidation = (patent) => {
  const validations = {
    minLengthValidation: patent?.toString()?.length >= 6,
  };

  const isInvalidField = anyFalseInObject(validations);

  return isInvalidField ? 'Campo patente inválido' : '';
};

const isValidPatent = (patent) =>
  [patentRequiredFieldValidation, patentFormatRequiredFieldValidation].every(
    (validation) => validation(patent) === '',
  );

const shouldSearchPatent = (patent, lastSearchedPatent) =>
  isValidPatent(patent) && patent !== lastSearchedPatent;

function FindVehicleSection() {
  const dispatch = useDispatch();
  const patentState = useSelector((state) => state.schedulerSteps.controls.patent);
  const patentError = useSelector((state) => state.schedulerSteps.controlsErrors.patent);
  const scrollRef = useRef(null);
  const lastSearchedPatent = useRef('');

  const loading = useSelector((state) => state.schedulerSteps.loading);
  const searchingVehicle = useSelector((state) => state.schedulerSteps.searchingVehicle);

  useEffect(() => {
    if (patentError && scrollRef.current) {
      scrollRef.current.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'center'
      });
    }
  }, [patentError]);

  const handleBlurPatentField = ({ name, value }) => {
    let errorMessage = '';
    errorMessage = patentRequiredFieldValidation(value);
    if (errorMessage === '') {
      errorMessage = patentFormatRequiredFieldValidation(value);
    }

    dispatch(
      setControlsErrors({
        controlsErrors: {
          [name]: errorMessage,
        },
      }),
    );
  };
  const onChange = (event) => {
    const { value, name } = event.target;
    dispatch(controlsChanged({ name, value }));
    if (name === 'patent') {
      handleBlurPatentField({ name, value });
    }
  };

  /* A valid plate is looked up to autocomplete the vehicle from SAP. The vehicle fields stay
     blocked until it answers, and no error is reported: without an answer the user just
     fills them manually. */
  const onBlur = () => {
    if (!shouldSearchPatent(patentState, lastSearchedPatent.current)) return;

    lastSearchedPatent.current = patentState;
    dispatch(getUserVehicleWithConectaApi({ patent: patentState }));
  };

  return (
    <Grid ref={scrollRef} item xs={12} sm={12} md={5} lg={2.8} sx={{ marginBottom: '20px' }}>
      <TextField
        id="patent"
        name="patent"
        key="patent"
        label="Patente"
        placeholder="EJ: ABCD12"
        variant="outlined"
        InputLabelProps={{
          sx: { ...darkerGreyOnForms },
        }}
        size="small"
        sx={SchedulerStepsStyles?.patentField}
        fullWidth
        value={patentState}
        onChange={onChange}
        onBlur={onBlur}
        error={!!patentError}
        helperText={patentHelperText(patentError, searchingVehicle)}
        FormHelperTextProps={{ 'aria-live': 'polite' }}
        InputProps={{ endAdornment: searchingAdornment(searchingVehicle) }}
        disabled={[loading, searchingVehicle].some(Boolean)}
      />
    </Grid>
  );
}

export default FindVehicleSection;
